import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const LBL = "text-[10px] uppercase tracking-[0.1em] text-[#6B7280]";
const blank = {
  txn_type: "Receipt", quantity: "", lot_number: "", expiry_date: "", txn_date: "",
  vendor: "", grn_no: "", unit_cost: "", issued_to: "", analyzer: "", reason: "",
};

export const StockDialog = ({ item: initial, meta, onClose, onSaved }) => {
  const [item, setItem] = useState(initial);
  const [form, setForm] = useState(blank);
  const [txns, setTxns] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setItem(initial);
    if (!initial) return;
    setForm({ ...blank, vendor: initial.preferred_vendor || "", analyzer: initial.analyzer || "" });
    api.get("/store/transactions", { params: { item_id: initial.item_id, days: 365, limit: 20 } })
      .then(({ data }) => setTxns(data)).catch(() => setTxns([]));
  }, [initial]);

  if (!item) return null;
  const isIn = (meta?.txn_types?.in || []).includes(form.txn_type);

  const submit = async () => {
    if (!Number(form.quantity)) return toast.error("Enter a quantity greater than zero");
    setBusy(true);
    try {
      const { data } = await api.post("/store/transactions", {
        item_id: item.item_id, ...form,
        quantity: Number(form.quantity),
        unit_cost: form.unit_cost === "" ? null : Number(form.unit_cost),
      });
      toast.success(`${form.txn_type} recorded — ${data.on_hand} ${item.stock_unit} on hand`);
      const [{ data: fresh }, { data: refreshed }] = await Promise.all([
        api.get("/store/transactions", { params: { item_id: item.item_id, days: 365, limit: 20 } }),
        api.get(`/store/items/${item.item_id}`),
      ]);
      setTxns(fresh);
      setItem(refreshed);
      setForm({ ...blank, txn_type: form.txn_type, vendor: form.vendor, analyzer: form.analyzer });
      onSaved?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="stock-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">{item.generic_name}</DialogTitle>
          <DialogDescription className="text-xs text-[#6B7280]">
            {item.code} · on hand <b>{item.on_hand} {item.stock_unit}</b> · min {item.min_level} · reorder{" "}
            {item.reorder_level} · max {item.max_level}. Every movement here updates the live stock instantly.
          </DialogDescription>
        </DialogHeader>

        {item.lots?.length > 0 && (
          <div className="rounded-md border border-[#E5E7EB] p-3" data-testid="stock-lots">
            <p className={LBL}>Lots in stock (first-expiry-first-out)</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {item.lots.map((lo) => (
                <span key={lo.lot_number} className="rounded-full border border-[#E5E7EB] bg-[#F9FAFB] px-2 py-0.5 text-[11px]">
                  {lo.lot_number} · {lo.quantity} {item.stock_unit}
                  {lo.expiry_date ? ` · exp ${lo.expiry_date}` : ""}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <Label className={LBL}>Movement</Label>
            <select value={form.txn_type} onChange={set("txn_type")} data-testid="txn-type"
              className="mt-1 h-10 w-full rounded-md border border-[#E5E7EB] bg-white px-2 text-sm">
              <optgroup label="Stock in">
                {(meta?.txn_types?.in || []).map((t) => <option key={t} value={t}>{t}</option>)}
              </optgroup>
              <optgroup label="Stock out">
                {(meta?.txn_types?.out || []).map((t) => <option key={t} value={t}>{t}</option>)}
              </optgroup>
            </select>
          </div>
          <div>
            <Label className={LBL}>Quantity ({item.stock_unit})</Label>
            <Input value={form.quantity} onChange={set("quantity")} className="mt-1" data-testid="txn-quantity" />
          </div>
          <div>
            <Label className={LBL}>Date</Label>
            <Input type="date" value={form.txn_date} onChange={set("txn_date")} className="mt-1" data-testid="txn-date" />
          </div>
          <div>
            <Label className={LBL}>Lot / batch no.{isIn && item.lot_tracked ? " *" : ""}</Label>
            <Input value={form.lot_number} onChange={set("lot_number")} className="mt-1" data-testid="txn-lot" />
          </div>
          {isIn ? (
            <>
              <div>
                <Label className={LBL}>Expiry date</Label>
                <Input type="date" value={form.expiry_date} onChange={set("expiry_date")} className="mt-1" data-testid="txn-expiry" />
              </div>
              <div>
                <Label className={LBL}>Vendor</Label>
                <Input value={form.vendor} onChange={set("vendor")} className="mt-1" data-testid="txn-vendor" />
              </div>
              <div>
                <Label className={LBL}>GRN / invoice no.</Label>
                <Input value={form.grn_no} onChange={set("grn_no")} className="mt-1" data-testid="txn-grn" />
              </div>
              <div>
                <Label className={LBL}>Unit cost</Label>
                <Input value={form.unit_cost} onChange={set("unit_cost")} className="mt-1" data-testid="txn-cost" />
              </div>
            </>
          ) : (
            <>
              <div>
                <Label className={LBL}>Issued to / bench</Label>
                <Input value={form.issued_to} onChange={set("issued_to")} className="mt-1" data-testid="txn-issued-to" />
              </div>
              <div>
                <Label className={LBL}>Analyzer</Label>
                <Input value={form.analyzer} onChange={set("analyzer")} className="mt-1" data-testid="txn-analyzer" />
              </div>
            </>
          )}
          <div className="sm:col-span-3">
            <Label className={LBL}>Reason / remarks</Label>
            <Input value={form.reason} onChange={set("reason")} className="mt-1" data-testid="txn-reason" />
          </div>
        </div>

        <Button onClick={submit} disabled={busy} data-testid="txn-save"
          className="w-fit bg-[#002FA7] hover:bg-[#00237C] text-white">
          {busy ? "Saving…" : `Record ${form.txn_type}`}
        </Button>

        <div className="mt-2">
          <p className={LBL}>Recent movements</p>
          <div className="mt-2 divide-y divide-[#E5E7EB] max-h-56 overflow-y-auto" data-testid="stock-history">
            {txns.length === 0 && <p className="text-sm text-[#6B7280] py-2">No movements recorded yet.</p>}
            {txns.map((t) => (
              <div key={t.txn_id} className="py-2 flex items-center justify-between text-xs">
                <span className="text-[#111827]">
                  {t.txn_date} · <b>{t.txn_type}</b> {t.quantity} {t.stock_unit}
                  {t.lot_number ? ` · lot ${t.lot_number}` : ""}
                </span>
                <span className="text-[#6B7280]">{t.performed_by}</span>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="stock-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
